import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, message, Row, Col, Card, Space, Modal } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { contactsAPI, familiesAPI } from '../../services/api';
import './ContactForm.css';

const { Option } = Select;
const { TextArea } = Input;

const ContactForm = ({ contact, onSuccess }) => {
  const [form] = Form.useForm();
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [phones, setPhones] = useState([{ id: Date.now(), phone_number: '', phone_type: 'mobile', is_primary: true }]);
  const [emails, setEmails] = useState([{ id: Date.now(), email_address: '', email_type: 'personal', is_primary: true }]);
  const [familyModalVisible, setFamilyModalVisible] = useState(false);
  const [subFamilyModalVisible, setSubFamilyModalVisible] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [newSubFamilyName, setNewSubFamilyName] = useState('');
  const queryClient = useQueryClient();

  const { data: familiesData } = useQuery('families', () => familiesAPI.getAll());
  const families = familiesData?.data?.data?.families || [];

  // Filtrer les sous-familles selon la famille sélectionnée
  const filteredSubFamilies = selectedFamily
    ? families.find(f => f.id === selectedFamily)?.subFamilies || []
    : families.flatMap(f => f.subFamilies || []);

  // Options pour le sélecteur de sous-famille (uniquement les sous-familles)
  const subFamilyOptions = selectedFamily
    ? filteredSubFamilies.map(sf => ({
        ...sf,
        isFamily: false
      }))
    : families.flatMap(f => f.subFamilies || []);

  const createMutation = useMutation(contactsAPI.create, {
    onSuccess: () => {
      message.success('Contact créé avec succès');
      onSuccess();
    },
    onError: (error) => {
      console.error('Create contact error:', error.response?.data);
      const errorMessage = error.response?.data?.errors?.[0]?.msg || 
                           error.response?.data?.message || 
                           'Erreur lors de la création';
      message.error(errorMessage);
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => contactsAPI.update(id, data),
    {
      onSuccess: () => {
        message.success('Contact mis à jour avec succès');
        onSuccess();
      },
      onError: (error) => {
        console.error('Update contact error:', error.response?.data);
        const errorMessage = error.response?.data?.errors?.[0]?.msg || 
                             error.response?.data?.message || 
                             'Erreur lors de la mise à jour';
        message.error(errorMessage);
      }
    }
  );

  const handleSubmit = async (values) => {
    // Transformer les données pour l'API
    const submitData = { ...values };
    
    // Gérer la sélection de famille et sous-famille
    if (values.family_filter) {
      // Une famille est sélectionnée dans le champ famille
      submitData.family_id = values.family_filter;
    }
    
    if (values.sub_family_id) {
      // Une sous-famille est sélectionnée, on trouve le family_id parent
      const allSubFamilies = families.flatMap(f => f.subFamilies || []);
      const selectedSubFamily = allSubFamilies.find(sf => sf.id === values.sub_family_id);
      if (selectedSubFamily && selectedSubFamily.family_id) {
        submitData.family_id = selectedSubFamily.family_id;
        submitData.sub_family_id = values.sub_family_id;
      }
    }
    
    // Supprimer les champs qui ne sont pas dans le modèle Contact
    delete submitData.family_filter;
    
    // S'assurer que les champs optionnels ont une valeur par défaut
    const optionalFields = ['address', 'wilaya', 'city', 'notes', 'position'];
    optionalFields.forEach(field => {
      if (submitData[field] === undefined || submitData[field] === '') {
        submitData[field] = null;
      }
    });
    
    // Ajouter les téléphones et emails
    const validPhones = phones.filter(p => p.phone_number.trim() !== '');
    const validEmails = emails.filter(e => e.email_address.trim() !== '');
    
    if (validPhones.length === 0) {
      message.error('Veuillez ajouter au moins un numéro de téléphone');
      return;
    }
    
    if (validEmails.length === 0) {
      message.error('Veuillez ajouter au moins une adresse email');
      return;
    }
    
    submitData.phones = validPhones.map(({ id, ...phone }) => phone);
    submitData.emails = validEmails.map(({ id, ...email }) => email);
    
    // Add random image URL for new contacts
    if (!contact) {
      const imageUrl = `https://picsum.photos/seed/${Math.random().toString(36).substring(7)}/150/150.jpg`;
      submitData.image_url = imageUrl;
    } else {
      // For existing contacts, keep current image or generate new one if needed
      if (!submitData.image_url) {
        submitData.image_url = contact.image_url || `https://picsum.photos/seed/${Math.random().toString(36).substring(7)}/150/150.jpg`;
      }
    }
    
    if (contact) {
      updateMutation.mutate({ id: contact.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleFamilyChange = (familyId) => {
    setSelectedFamily(familyId);
    // Réinitialiser la sous-famille quand la famille change
    form.setFieldsValue({ sub_family_id: undefined });
  };

  // Mutations pour créer des familles et sous-familles
  const createFamilyMutation = useMutation(
    (data) => familiesAPI.create(data),
    {
      onSuccess: (response) => {
        message.success('Famille créée avec succès');
        setFamilyModalVisible(false);
        setNewFamilyName('');
        
        // Invalider le cache pour forcer le rafraîchissement
        queryClient.invalidateQueries('families').then(() => {
          // Après rafraîchissement, créer automatiquement une sous-famille "GÉNÉRAL"
          const newFamilyId = response.data?.data?.family?.id;
          if (newFamilyId) {
            // Créer la sous-famille "GÉNÉRAL" par défaut
            familiesAPI.createSubFamily(newFamilyId, { name: 'GÉNÉRAL' })
              .then((subFamilyResponse) => {
                // Invalider à nouveau le cache pour inclure la nouvelle sous-famille
                queryClient.invalidateQueries('families').then(() => {
                  // Sélectionner la nouvelle famille et la sous-famille "GÉNÉRAL"
                  setSelectedFamily(newFamilyId);
                  form.setFieldsValue({ 
                    family_filter: newFamilyId,
                    sub_family_id: subFamilyResponse.data?.data?.subFamily?.id
                  });
                  message.success('Sous-famille "GÉNÉRAL" créée automatiquement');
                });
              })
              .catch((error) => {
                console.error('Error creating default sub-family:', error);
                // En cas d'erreur, sélectionner seulement la famille
                setSelectedFamily(newFamilyId);
                form.setFieldsValue({ 
                  family_filter: newFamilyId,
                  sub_family_id: undefined 
                });
              });
          }
        });
      },
      onError: (error) => {
        console.error('Create family error:', error.response?.data);
        const errorMessage = error.response?.data?.message || 'Erreur lors de la création de la famille';
        message.error(errorMessage);
      }
    }
  );

  const createSubFamilyMutation = useMutation(
    ({ family_id, ...data }) => familiesAPI.createSubFamily(family_id, data),
    {
      onSuccess: (response) => {
        message.success('Sous-famille créée avec succès');
        setSubFamilyModalVisible(false);
        setNewSubFamilyName('');
        
        // Invalider le cache pour forcer le rafraîchissement
        queryClient.invalidateQueries('families').then(() => {
          // Après rafraîchissement, sélectionner la nouvelle sous-famille
          const newSubFamilyId = response.data?.data?.subFamily?.id;
          if (newSubFamilyId) {
            form.setFieldsValue({ 
              sub_family_id: newSubFamilyId
            });
          }
        });
      },
      onError: (error) => {
        console.error('Create sub-family error:', error.response?.data);
        const errorMessage = error.response?.data?.message || 'Erreur lors de la création de la sous-famille';
        message.error(errorMessage);
      }
    }
  );

  const handleAddFamily = () => {
    if (!newFamilyName.trim()) {
      message.error('Veuillez entrer un nom de famille');
      return;
    }
    createFamilyMutation.mutate({ name: newFamilyName.trim() });
  };

  const handleAddSubFamily = () => {
    if (!newSubFamilyName.trim()) {
      message.error('Veuillez entrer un nom de sous-famille');
      return;
    }
    if (!selectedFamily) {
      message.error('Veuillez d\'abord sélectionner une famille');
      return;
    }
    createSubFamilyMutation.mutate({ 
      name: newSubFamilyName.trim(),
      family_id: selectedFamily 
    });
  };

  // Effet pour initialiser le formulaire avec les valeurs du contact ou les valeurs par défaut
  useEffect(() => {
    if (contact) {
      const formValues = { ...contact };
      
      // Si le contact a une famille, on la met dans family_filter
      if (contact.family_id && !contact.sub_family_id) {
        formValues.family_filter = contact.family_id;
        setSelectedFamily(contact.family_id);
      } else if (contact.sub_family_id) {
        // Si le contact a une sous-famille, on la met directement
        formValues.sub_family_id = contact.sub_family_id;
        // Trouver la famille parente de la sous-famille sélectionnée
        const parentFamily = families.find(f => 
          f.subFamilies?.some(sf => sf.id === contact.sub_family_id)
        );
        if (parentFamily) {
          setSelectedFamily(parentFamily.id);
        }
      } else {
        // Si ni famille ni sous-famille, on ne met rien dans sub_family_id
        formValues.sub_family_id = undefined;
      }
      
      // Initialiser les téléphones et emails
      if (contact.phones && contact.phones.length > 0) {
        setPhones(contact.phones.map(p => ({ ...p, id: Date.now() + Math.random() })));
      }
      if (contact.emails && contact.emails.length > 0) {
        setEmails(contact.emails.map(e => ({ ...e, id: Date.now() + Math.random() })));
      }
      
      form.setFieldsValue(formValues);
    } else {
      // Pour un nouveau contact, ne définir les valeurs par défaut que si aucune famille n'est déjà sélectionnée
      const currentFamilyFilter = form.getFieldValue('family_filter');
      const currentSubFamily = form.getFieldValue('sub_family_id');
      
      if (!currentFamilyFilter && !currentSubFamily && families.length > 0) {
        const defaultFamily = families.find(f => f.name === 'GÉNÉRAL');
        if (defaultFamily) {
          const defaultSubFamily = defaultFamily.subFamilies?.find(sf => sf.name === 'GÉNÉRAL');
          if (defaultSubFamily) {
            // Sélectionner la sous-famille "GÉNÉRAL" par défaut
            form.setFieldsValue({ 
              sub_family_id: defaultSubFamily.id,
              family_filter: defaultFamily.id
            });
            setSelectedFamily(defaultFamily.id);
          } else {
            // Si pas de sous-famille "GÉNÉRAL", sélectionner la famille "GÉNÉRAL"
            form.setFieldsValue({ 
              family_filter: defaultFamily.id
            });
            setSelectedFamily(defaultFamily.id);
          }
        }
      }
    }
  }, [contact, form]); // Retirer 'families' des dépendances pour éviter la réinitialisation

  const addPhone = () => {
    setPhones([...phones, { id: Date.now(), phone_number: '', phone_type: 'mobile', is_primary: false }]);
  };

  const removePhone = (id) => {
    if (phones.length > 1) {
      setPhones(phones.filter(p => p.id !== id));
    }
  };

  const updatePhone = (id, field, value) => {
    const updatedPhones = phones.map(p => {
      if (p.id === id) {
        const updated = { ...p, [field]: value };
        // Si on définit ce téléphone comme primaire, décocher les autres
        if (field === 'is_primary' && value === true) {
          return updated;
        }
        return updated;
      }
      // Si un autre téléphone est mis à jour comme primaire, décocher celui-ci
      if (field === 'is_primary' && value === true) {
        return { ...p, is_primary: false };
      }
      return p;
    });
    setPhones(updatedPhones);
  };

  const addEmail = () => {
    setEmails([...emails, { id: Date.now(), email_address: '', email_type: 'personal', is_primary: false }]);
  };

  const removeEmail = (id) => {
    if (emails.length > 1) {
      setEmails(emails.filter(e => e.id !== id));
    }
  };

  const updateEmail = (id, field, value) => {
    const updatedEmails = emails.map(e => {
      if (e.id === id) {
        const updated = { ...e, [field]: value };
        // Si on définit cet email comme primaire, décocher les autres
        if (field === 'is_primary' && value === true) {
          return updated;
        }
        return updated;
      }
      // Si un autre email est mis à jour comme primaire, décocher celui-ci
      if (field === 'is_primary' && value === true) {
        return { ...e, is_primary: false };
      }
      return e;
    });
    setEmails(updatedEmails);
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={contact}
    >
      <Form.Item
        name="first_name"
        label="Prénom"
        rules={[{ required: true, message: 'Veuillez saisir le prénom' }]}
      >
        <Input placeholder="Prénom" />
      </Form.Item>

      <Form.Item
        name="last_name"
        label="Nom"
        rules={[{ required: true, message: 'Veuillez saisir le nom' }]}
      >
        <Input placeholder="Nom" />
      </Form.Item>

      <Card title="Téléphones" size="small" style={{ marginBottom: 16 }}>
        {phones.map((phone, index) => (
          <Row key={phone.id} gutter={16} style={{ marginBottom: 8 }}>
            <Col span={8}>
              <Input
                placeholder="Numéro de téléphone"
                value={phone.phone_number}
                onChange={(e) => updatePhone(phone.id, 'phone_number', e.target.value)}
              />
            </Col>
            <Col span={6}>
              <Select
                value={phone.phone_type}
                onChange={(value) => updatePhone(phone.id, 'phone_type', value)}
                style={{ width: '100%' }}
              >
                <Select.Option value="mobile">Mobile</Select.Option>
                <Select.Option value="home">Domicile</Select.Option>
                <Select.Option value="work">Travail</Select.Option>
                <Select.Option value="other">Autre</Select.Option>
              </Select>
            </Col>
            <Col span={6}>
              <Space>
                <input
                  type="checkbox"
                  checked={phone.is_primary}
                  onChange={(e) => updatePhone(phone.id, 'is_primary', e.target.checked)}
                />
                <span>Principal</span>
              </Space>
            </Col>
            <Col span={4}>
              {phones.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removePhone(phone.id)}
                />
              )}
            </Col>
          </Row>
        ))}
        <Button type="dashed" icon={<PlusOutlined />} onClick={addPhone} style={{ width: '100%' }}>
          Ajouter un téléphone
        </Button>
      </Card>

      <Card title="Emails" size="small" style={{ marginBottom: 16 }}>
        {emails.map((email, index) => (
          <Row key={email.id} gutter={16} style={{ marginBottom: 8 }}>
            <Col span={8}>
              <Input
                placeholder="Adresse email"
                value={email.email_address}
                onChange={(e) => updateEmail(email.id, 'email_address', e.target.value)}
              />
            </Col>
            <Col span={6}>
              <Select
                value={email.email_type}
                onChange={(value) => updateEmail(email.id, 'email_type', value)}
                style={{ width: '100%' }}
              >
                <Select.Option value="personal">Personnel</Select.Option>
                <Select.Option value="work">Professionnel</Select.Option>
                <Select.Option value="other">Autre</Select.Option>
              </Select>
            </Col>
            <Col span={6}>
              <Space>
                <input
                  type="checkbox"
                  checked={email.is_primary}
                  onChange={(e) => updateEmail(email.id, 'is_primary', e.target.checked)}
                />
                <span>Principal</span>
              </Space>
            </Col>
            <Col span={4}>
              {emails.length > 1 && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeEmail(email.id)}
                />
              )}
            </Col>
          </Row>
        ))}
        <Button type="dashed" icon={<PlusOutlined />} onClick={addEmail} style={{ width: '100%' }}>
          Ajouter un email
        </Button>
      </Card>

      <Form.Item
        name="company"
        label="Société"
        rules={[{ required: true, message: 'Veuillez saisir la société' }]}
      >
        <Input placeholder="Société" />
      </Form.Item>

      <Form.Item
        name="position"
        label="Poste"
      >
        <Input placeholder="Poste" />
      </Form.Item>

      <Form.Item
        name="address"
        label="Adresse"
      >
        <TextArea placeholder="Adresse" rows={2} />
      </Form.Item>

      <Form.Item
        name="wilaya"
        label="Wilaya"
      >
        <Input placeholder="Wilaya" />
      </Form.Item>

      <Form.Item
        name="city"
        label="Ville"
      >
        <Input placeholder="Ville" />
      </Form.Item>

      <Form.Item
        name="family_filter"
        label="Famille"
      >
        <Input.Group compact>
          <Select 
            placeholder="Toutes les familles" 
            allowClear
            onChange={handleFamilyChange}
            value={selectedFamily}
            style={{ width: 'calc(100% - 32px)' }}
          >
            {families.map(family => (
              <Option key={family.id} value={family.id}>
                {family.name}
              </Option>
            ))}
          </Select>
          <Button 
            icon={<PlusOutlined />} 
            onClick={() => setFamilyModalVisible(true)}
            style={{ width: '32px' }}
            title="Ajouter une famille"
          />
        </Input.Group>
      </Form.Item>

      <Form.Item
        name="sub_family_id"
        label="Sous-famille"
      >
        <Input.Group compact>
          <Select 
            placeholder="Sélectionner une sous-famille" 
            allowClear
            style={{ width: 'calc(100% - 32px)' }}
          >
            {subFamilyOptions.map(option => (
              <Option 
                key={option.id} 
                value={option.id}
              >
                {option.name}
              </Option>
            ))}
          </Select>
          <Button 
            icon={<PlusOutlined />} 
            onClick={() => setSubFamilyModalVisible(true)}
            style={{ width: '32px' }}
            title="Ajouter une sous-famille"
          />
        </Input.Group>
      </Form.Item>

      <Form.Item
        name="notes"
        label="Notes"
      >
        <TextArea placeholder="Notes" rows={4} />
      </Form.Item>

      {/* Modal pour ajouter une famille */}
      <Modal
        title="Ajouter une famille"
        open={familyModalVisible}
        onOk={handleAddFamily}
        onCancel={() => {
          setFamilyModalVisible(false);
          setNewFamilyName('');
        }}
        confirmLoading={createFamilyMutation.isLoading}
      >
        <Input
          placeholder="Nom de la famille"
          value={newFamilyName}
          onChange={(e) => setNewFamilyName(e.target.value)}
          onPressEnter={handleAddFamily}
        />
      </Modal>

      {/* Modal pour ajouter une sous-famille */}
      <Modal
        title="Ajouter une sous-famille"
        open={subFamilyModalVisible}
        onOk={handleAddSubFamily}
        onCancel={() => {
          setSubFamilyModalVisible(false);
          setNewSubFamilyName('');
        }}
        confirmLoading={createSubFamilyMutation.isLoading}
      >
        <Input
          placeholder="Nom de la sous-famille"
          value={newSubFamilyName}
          onChange={(e) => setNewSubFamilyName(e.target.value)}
          onPressEnter={handleAddSubFamily}
        />
        {!selectedFamily && (
          <div style={{ color: '#ff4d4f', marginTop: 8, fontSize: '12px' }}>
            Veuillez d'abord sélectionner une famille
          </div>
        )}
      </Modal>

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={isLoading} block>
          {contact ? 'Mettre à jour' : 'Créer'} le contact
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ContactForm;
